import React, { useState } from 'react';
import { 
  FaFileContract, FaCheckCircle, FaChevronDown, FaUndoAlt, FaShieldAlt 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const RefundAndCancellationPolicy = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "Are advisory and consulting fees refundable?",
      a: "Professional advisory fees, consultation charges, and personalized wealth planning service fees are generally non-refundable once the advisory session has been conducted or the deliverables have been initiated."
    },
    {
      q: "What is the cancellation policy for subscription-based services?",
      a: "Subscription-based financial wellness or portfolio tracking services can be canceled before the next billing cycle. Cancellations take effect at the end of the current active billing period."
    },
    {
      q: "How are refunds handled for third-party investment products or loans?",
      a: "Since Lotoswealth acts as an advisory and facilitation partner for third-party financial institutions, mutual funds, or loans, any refunds, application fee adjustments, or reversals are subject directly to the policies of the respective banks or asset management companies."
    },
    {
      q: "How can I request a cancellation or refund review?",
      a: "You can submit a formal request by reaching out to our support team through our Contact Us page with your transaction details and reason for the request."
    }
  ];

  return (
    <main className="refund-policy-page">
      <Helmet>
        {/* Basic SEO */}
        <title>Refund and Cancellation Policy | Service Terms | Lotoswealth</title>

        <meta
          name="description"
          content="Review the Refund and Cancellation Policy for Lotoswealth. Understand our terms regarding advisory fees, service cancellations, subscription terms, and third-party financial products."
        />

        <meta
          name="keywords"
          content="Refund Policy, Cancellation Policy, Lotoswealth Refunds, Service Cancellation, Advisory Fee Refund, Wealth Management Terms, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link
          rel="canonical"
          href="https://www.lotoswealth.com/refund-and-cancellation-policy"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta
          property="og:url"
          content="https://www.lotoswealth.com/refund-and-cancellation-policy"
        />
        <meta
          property="og:title"
          content="Refund and Cancellation Policy | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Read the terms and conditions governing service cancellations and refunds for Lotoswealth's advisory and financial planning offerings."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Refund and Cancellation Policy | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Understand our guidelines regarding subscription cancellations, advisory fees, and third-party financial service processing."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>

      {/* =========================================
          1. HERO SECTION
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '60vh', minHeight: '450px', overflow: 'hidden', backgroundColor: '#0a192f' }}>
        
        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 opacity-25">
          <img 
            src="/images/lotoswealth-bg.jpg" 
            alt="Refund and Cancellation Background" 
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Refund & Cancellation Policy
          </h1>
          <p className="hero-description animate-slide-up delay-2 text-white mx-auto" style={{ maxWidth: '800px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            At Lotoswealth, we strive to deliver transparent, high-quality financial advisory and wealth management services. Please review our policy regarding cancellations and refunds.
          </p>
        </div>
      </div>

      {/* =========================================
          2. CORE POLICY OVERVIEW
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Transparent Guidelines for Service Engagement
              </h2>
              <p className="text-muted line-height-lg">
                Because our offerings involve personalized wealth planning, portfolio management setup, tax advisory, and loan facilitation, our refund and cancellation terms are structured to reflect the professional nature of our advisory services.
              </p>
              <p className="text-muted line-height-lg">
                We encourage all clients to thoroughly review service agreements and subscription details before initiating transactions.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Key Principles</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Advisory Nature:</strong> Fees cover professional expertise and consultation time.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Subscription Control:</strong> Cancel recurring services prior to renewal cycles.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Third-Party Terms:</strong> Partner bank and mutual fund fee rules apply separately.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Dedicated Support:</strong> Assistance available for all legitimate billing inquiries.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. DETAILED POLICY CLAUSES
      ========================================= */}
      <section className="py-5 bg-white">
        <div className="container py-4" style={{ maxWidth: '900px' }}>
          
          <div className="mb-5 animate-slide-up delay-1">
            <h3 className="fw-bold text-dark mb-3">1. Service Cancellation Policy</h3>
            <p className="text-muted">
              Clients may choose to cancel ongoing advisory services, consultation bookings, or recurring subscription packages by notifying our support team in advance. For subscription-based plans, cancellations must be completed prior to the upcoming billing date to prevent subsequent charges. Services will remain active until the conclusion of the paid billing period.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-2">
            <h3 className="fw-bold text-dark mb-3">2. Refund Eligibility on Advisory Fees</h3>
            <p className="text-muted">
              Professional fees paid for one-on-one wealth management consultations, financial planning reports, or custom advisory deliverables are generally non-refundable once our experts have commenced work or delivered the consultation session. Refunds are considered solely in exceptional circumstances where Lotoswealth fails to deliver the contracted service due to internal technical failures or unresolvable scheduling conflicts.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-3">
            <h3 className="fw-bold text-dark mb-3">3. Third-Party Products and Loan Processing Fees</h3>
            <p className="text-muted">
              Lotoswealth acts as a facilitator for home loans, car loans, mutual funds, and alternative investments managed by external financial institutions, banks, or asset management companies. Any statutory processing fees, loan application charges, or investment deposits paid directly to third-party institutions are governed strictly by the respective institution's refund and cancellation policies. Lotoswealth assumes no liability for third-party fee withholdings.
            </p>
          </div>

          <div className="mb-5 animate-slide-up delay-4">
            <h3 className="fw-bold text-dark mb-3">4. Processing Timeline for Approved Refunds</h3>
            <p className="text-muted">
              In the event that a refund is explicitly approved by Lotoswealth management, the amount will be processed through the original method of payment within 7 to 10 business days. Transaction processing times may vary depending on banking institutions.
            </p>
          </div>

        </div>
      </section>

      {/* =========================================
          4. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding our refund and cancellation terms</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button 
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown 
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          5. FINAL CTA SECTION
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Need Help With a Billing Inquiry?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
  If you have questions regarding our refund policy or need assistance with a transaction, feel free to contact our support team.
</p>
          <a href="/contact-us" className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3 text-decoration-none d-inline-block">
            Contact Us Today
          </a>
        </div>
      </section>

    </main>
  );
};

export default RefundAndCancellationPolicy;